import React from 'react';
import PropTypes from 'prop-types';
import Button from '@loggi/ui/components/button';
import Col from '@loggi/ui/components/col';
import Divider from '@loggi/ui/components/divider';
import { CIRCLE_SHAPE } from '@loggi/ui/components/image/types';
import colors from '@loggi/ui/components/utils/colors';
import icons from '@loggi/ui/components/utils/icons';
import { privatePackageTrackerModel } from 'app.models';
import get from 'lodash/get';
import AddressText from 'shared/address-text/address-text.component';
import Card from 'shared/card/card.component';
import StatusCard from 'shared/card/status-card/status-card.component';
import Text from '@loggi/ui/components/text';
import CanNotEditMessage from './can-not-edit-message/can-not-edit-message.component';
import ConfirmOriginalAddress from './confirm-original-address/confirm-original-address.component';
import DriverInfo from './driver-info/driver-info.component';
import {
  BLOCKED_EDITION_CONFIRM_ADDRESS_BUTTON_VALUE,
  BLOCKED_EDITION_DENY_ADDRESS_BUTTON_VALUE,
  BLOCKED_EDITION_QUESTION_TEXT_TO_CONFIRM_ADDRESS,
  CONFIRM_ADDRESS_BUTTON_VALUE,
  DENY_ADDRESS_BUTTON_VALUE,
  PICKUP_INSTRUCTIONS_TEXT_VALUE,
  QUESTION_TEXT_TO_CONFIRM_ADDRESS,
  TEST_ID_EDIT_BUTTON
} from './main-package-info-card.constants';
import styles from './main-package-info-card.styles.scss';
import RecipientPickupText from '../../../shared/recipient-pickup-text/recipient-pickup-text.component';

export default function MainPackageInfoCard({
  privatePackageTracker,
  driverInfo,
  isAddressCorrect,
  canEditAddress,
  canConfirmOriginalAddress,
  canShowRecipientPickupInfo,
  onClickToChangeAddress,
  onClickToDenyOriginalAddress,
  onClickToConfirmOriginalAddress,
  statusInfo,
  receivedBy,
  isOriginLoggiApp
}) {
  const { destinationInfo } = privatePackageTracker;
  const { redispatchInfo } = privatePackageTracker;
  const { redispatchAgency } = redispatchInfo;
  const driverPhotoUrl = get(driverInfo, 'photoUrl.default', null);
  const driverFirstName = get(driverInfo, 'firstName', null);
  const showConfirmAddressQuestion =
    isAddressCorrect === null &&
    canConfirmOriginalAddress &&
    !canShowRecipientPickupInfo;
  const showEditButton = isAddressCorrect !== null && canEditAddress;
  const showCanNotEditMessage = !canEditAddress && isAddressCorrect === false;
  let questionText = QUESTION_TEXT_TO_CONFIRM_ADDRESS;
  let confirmButtonText = CONFIRM_ADDRESS_BUTTON_VALUE;
  let denyButtonText = DENY_ADDRESS_BUTTON_VALUE;
  const pickupText = PICKUP_INSTRUCTIONS_TEXT_VALUE;

  if (!canEditAddress) {
    questionText = BLOCKED_EDITION_QUESTION_TEXT_TO_CONFIRM_ADDRESS;
    confirmButtonText = BLOCKED_EDITION_CONFIRM_ADDRESS_BUTTON_VALUE;
    denyButtonText = BLOCKED_EDITION_DENY_ADDRESS_BUTTON_VALUE;
  }

  return (
    <Card gutter="onehalf" className={styles.mainPackageInfoCard}>
      <Divider size={4} color={colors.type.SMOKE_100} />
      <StatusCard
        statusInfo={statusInfo}
        receivedBy={receivedBy}
        showEtaMessage={!canShowRecipientPickupInfo}
        isOriginLoggiApp={isOriginLoggiApp}
      />
      <Col className="separator">
        <Divider color={colors.type.SMOKE_100} gutter="normal" />
      </Col>
      {canShowRecipientPickupInfo && (
        <Col>
          <Col>
            <RecipientPickupText
              trackingCode={redispatchInfo.trackingCode}
              maxPickupTime={redispatchInfo.maxPickupTime}
              gutter={{ top: 'onehalf' }}
            />
          </Col>
          <Col className="separator" gutter={{ top: 'onehalf' }}>
            <Divider color={colors.type.SMOKE_100} gutter="normal" />
          </Col>
          <Col
            gutter={{ top: 'onehalf' }}
            justifyContent="between"
            alignItems="center"
          >
            <Text size="300" color={colors.type.STORM_500}>
              {pickupText}
            </Text>
          </Col>
        </Col>
      )}
      <Col
        gutter={{ top: 'onehalf' }}
        justifyContent="between"
        alignItems="center"
      >
        {canShowRecipientPickupInfo ? (
          <AddressText
            name={redispatchAgency.name}
            firstLine={redispatchAgency.firstLine}
            secondLine={redispatchAgency.secondLine}
            hasIcon
          />
        ) : (
          <AddressText
            firstLine={destinationInfo.firstLine}
            secondLine={destinationInfo.secondLine}
            complement={destinationInfo.complement}
            hasIcon
          />
        )}
        {showEditButton && (
          <Button
            data-testid={TEST_ID_EDIT_BUTTON}
            size="small"
            style="outline"
            shape={CIRCLE_SHAPE}
            icon={icons.type.EDIT}
            onClick={onClickToChangeAddress}
          />
        )}
      </Col>
      {showConfirmAddressQuestion && (
        <Col gutter={{ top: 'onehalf' }}>
          <ConfirmOriginalAddress
            onClickToDeny={onClickToDenyOriginalAddress}
            onClickToConfirm={onClickToConfirmOriginalAddress}
            questionText={questionText}
            confirmButtonText={confirmButtonText}
            denyButtonText={denyButtonText}
          />
        </Col>
      )}
      {driverFirstName && (
        <DriverInfo photoUrl={driverPhotoUrl} name={driverFirstName} />
      )}
      {showCanNotEditMessage && <CanNotEditMessage />}
    </Card>
  );
}

MainPackageInfoCard.displayName = 'MainPackageInfoCard';

MainPackageInfoCard.propTypes = {
  privatePackageTracker: PropTypes.shape(privatePackageTrackerModel).isRequired,
  driverInfo: PropTypes.shape({
    photoUrl: {
      default: PropTypes.string.isRequired
    },
    firstName: PropTypes.string
  }),
  statusInfo: PropTypes.shape({
    companyName: PropTypes.string,
    eta: PropTypes.number,
    relativeEta: PropTypes.number,
    currentStatus: PropTypes.shape({
      error: PropTypes.number,
      inProgress: PropTypes.bool,
      destination: PropTypes.string
    }),
    shipperStatus: PropTypes.shape({
      code: PropTypes.number,
      label: PropTypes.string,
      description: PropTypes.string,
      actionRequired: PropTypes.shape({
        reasonLabel: PropTypes.string,
        reasonDescription: PropTypes.string
      })
    })
  }).isRequired,
  isAddressCorrect: PropTypes.bool.isRequired,
  canEditAddress: PropTypes.bool.isRequired,
  canConfirmOriginalAddress: PropTypes.bool.isRequired,
  canShowRecipientPickupInfo: PropTypes.bool.isRequired,
  onClickToChangeAddress: PropTypes.func.isRequired,
  onClickToDenyOriginalAddress: PropTypes.func.isRequired,
  onClickToConfirmOriginalAddress: PropTypes.func.isRequired,
  receivedBy: PropTypes.string,
  isOriginLoggiApp: PropTypes.bool
};

MainPackageInfoCard.defaultProps = {
  driverInfo: {
    photoUrl: {
      default: null
    },
    firstName: null
  },
  receivedBy: null,
  isOriginLoggiApp: false
};
